import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import ResourceAvailabilityTimeline from '../../../components/ResourceTimeline/ResourceTimeline'
import API from '../../../services/API'
import { Circle, Report } from '@mui/icons-material'

import { BLOCK_COLORS, BLOCK_LABELS } from './vehicleAvailabilityConstants'
import VehicleWeekSchedulePDF from '../../../components/PDF/VehicleWeekSchedulePDF'
import PrintButton from '../../../components/PDF/PrintButton'
import usePrintChrome from '../../../components/PDF/usePrintChrome'
import { downloadPdf } from '../../../helpers/downloadPdf'

// Week-at-a-glance of what every vehicle is committed to — PTS duties, event
// deployments, and unavailability. Click a vehicle (or any of its blocks) to
// open its fleet page.
export default function VehicleAvailabilityPage({ vehicles }) {
  const navigate = useNavigate()
  const [weekStart, setWeekStart] = useState(moment().startOf('week'))
  const [blocks, setBlocks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const rangeStart = weekStart.clone().toDate()
  const rangeEnd = weekStart.clone().add(7, 'days').toDate()

  useEffect(() => {
    setLoading(true)
    setError(null)
    API.get(
      `/fleet/vehicle-availability?start=${weekStart.format('YYYY-MM-DD')}&end=${weekStart
        .clone()
        .add(6, 'days')
        .format('YYYY-MM-DD')}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setBlocks(res.data || [])
        } else {
          setError('Unable to load vehicle usage. Please try again.')
        }
      })
      .catch((e) => {
        console.error(e)
        setError('Unable to load vehicle usage. Please try again.')
      })
      .finally(() => setLoading(false))
  }, [weekStart])

  const resources = useMemo(
    () =>
      vehicles.map((vehicle) => ({
        id: vehicle._id,
        title: vehicle.callSign || vehicle.regPlate,
        subtitle: `${vehicle.regPlate} • ${vehicle.vehicleType || 'Vehicle'}`,
        status: vehicle.status,
        activeDefects: vehicle.activeDefects,
        motExpiry: vehicle.motExpiry,
        meta: vehicle,
      })),
    [vehicles],
  )

  const items = useMemo(
    () =>
      blocks.map((block) => ({
        id: block._id,
        resourceId: block.vehicleId,
        start: block.start,
        end: block.end,
        type: block.type,
        label: block.label,
        status: block.status,
        meta: block,
      })),
    [blocks],
  )

  const openVehicle = (vehicleId) => {
    if (vehicleId) navigate(`/fleet/${vehicleId}`)
  }

  const chrome = usePrintChrome()

  const handlePrint = async () => {
    await downloadPdf(
      <VehicleWeekSchedulePDF
        vehicles={vehicles}
        blocks={blocks}
        weekStart={weekStart.toISOString()}
        {...chrome}
      />,
      `AmbuNet_Vehicle_Availability_wc_${weekStart.format('YYYY-MM-DD')}.pdf`,
    )
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        gap={1}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Vehicle Availability
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Everything each vehicle is committed to this week — click a vehicle
            to open its page.
          </Typography>
        </Box>

        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <PrintButton
            onPrint={handlePrint}
            variant="outlined"
            disabled={loading}
            sx={{ whiteSpace: 'nowrap' }}
          />
          <ButtonGroup
            sx={{
              flex: { xs: 1, md: 'none' },
              '& .MuiButton-root': { flex: { xs: 1, md: 'none' } },
            }}
          >
            <Button
              onClick={() =>
                setWeekStart((prev) => prev.clone().subtract(1, 'week'))
              }
            >
              Previous
            </Button>

            <Button onClick={() => setWeekStart(moment().startOf('week'))}>
              This Week
            </Button>

            <Button
              onClick={() => setWeekStart((prev) => prev.clone().add(1, 'week'))}
            >
              Next
            </Button>
          </ButtonGroup>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {Object.entries(BLOCK_LABELS).map(([type, label]) => (
          <Stack key={type} direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                backgroundColor: BLOCK_COLORS[type],
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      )}

      {/* The timeline is ~1100px wide; on phones it scrolls sideways inside
          this box rather than pushing the page out. */}
      <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
        <ResourceAvailabilityTimeline
          resources={resources}
          items={items}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          getItemColor={(item) => BLOCK_COLORS[item.type] || 'grey.500'}
          onItemClick={(item, resource) => openVehicle(resource?.id)}
          onRowClick={(resource) => openVehicle(resource?.id)}
          renderResource={(resource) => {
            const getStatusColor = () => {
              const { status } = resource
              switch (status) {
                case 'Operational':
                  return 'success'
                default:
                  return 'error'
              }
            }

            const hasWarning = !!resource.activeDefects

            return (
              <Stack spacing={0.25} width="100%">
                <Stack
                  direction="row"
                  gap={1}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" fontWeight={700}>
                    {resource.title}
                  </Typography>
                  <Stack direction="row" gap={0}>
                    {hasWarning && <Report color="error" />}
                    <Circle color={getStatusColor()} />
                  </Stack>
                </Stack>
                {resource.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {resource.subtitle}
                  </Typography>
                )}
              </Stack>
            )
          }}
        />
      </Box>
    </Stack>
  )
}
